/*  UI  */
import { Button, ButtonProps, Flex } from '@auviknetworks/iceberg-ui';
import { useUserTheme } from 'hooks/dataAccess/User/userTheme';
import MuiMarkdown from 'mui-markdown';

/*  React  */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { templateInterpolation } from '../Helpers/templateInterpolation';

/* internal */
import { Action, type EmptyPanel as EmptyPanelType, Variables } from '../types';
import { Panel } from './Panel';
import { usePanelContext } from './PanelContext';

interface EmptyStateProps {
    /** The content displayed in the empty state. Accepts markdown, and is interpolated with the context. */
    content: string;
    /** A set of actions displayed as buttons. */
    actions?: Action[];
    /** The context used to interpolate the content. */
    context?: Variables;
    /** The image displayed in the empty state. Default is a graph. */
    image?: 'graph' | 'calendar' | 'warning';
}

export const EmptyState = ({
    content,
    actions,
    context = {},
    image = 'graph'
}: EmptyStateProps) => {
    const userTheme = useUserTheme();
    const { t } = useTranslation();

    const imageSrc = {
        calendar: `/images/${userTheme}/emptyState/EmptyStateCalendarIcon.svg`,
        graph: `/images/${userTheme}/emptyState/EmptyStateIcon.svg`,
        warning: `/images/${userTheme}/emptyState/WarningTriangle.svg`
    };

    return (
        <Flex
            sx={{
                alignItems: 'center',
                height: '100%',
                justifyContent: 'center'
            }}
        >
            <Flex
                sx={{
                    alignItems: 'center',
                    flexDirection: 'column',
                    maxWidth: '512px',
                    padding: '24px'
                }}
            >
                <img
                    src={imageSrc[image]}
                    alt={t('altText.emptyStateIcon')}
                    style={{
                        paddingBottom: '32px',
                        width: '164px'
                    }}
                />
                <Flex
                    sx={{
                        marginBottom: '32px',
                        textAlign: 'center',
                        whiteSpace: 'pre-line'
                    }}
                >
                    <MuiMarkdown>
                        {templateInterpolation(t(content), context)}
                    </MuiMarkdown>
                </Flex>
                {actions && (
                    <Flex sx={{ gap: '16px' }}>
                        {actions.map(
                            ({
                                id,
                                label,
                                href,
                                target,
                                format: {
                                    variant = 'primary',
                                    size = 'medium'
                                } = {}
                            }) => (
                                <Link
                                    key={label}
                                    to={href}
                                    target={target}
                                    data-testid={id || label}
                                    data-trackid={id || label}
                                >
                                    <Button
                                        data-trackid={`${id || label}Btn`}
                                        variant={
                                            variant as ButtonProps['variant']
                                        }
                                        target={target}
                                        size={size as ButtonProps['size']}
                                    >
                                        {t(label)}
                                    </Button>
                                </Link>
                            )
                        )}
                    </Flex>
                )}
            </Flex>
        </Flex>
    );
};

export const EmptyPanelContent = () => {
    const { schema, context } = usePanelContext<EmptyPanelType>();

    return (
        <EmptyState
            content={schema.content}
            actions={schema.actions}
            context={context.variables}
            image={schema.format?.image}
        />
    );
};

export const EmptyPanel = ({ panel }: { panel: EmptyPanelType }) => (
    <Panel<EmptyPanelType> canFullScreen={false} panel={panel}>
        <EmptyPanelContent />
    </Panel>
);
